// Selector de mejores terceros que clasifican a octavos de final
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check, AlertCircle } from 'lucide-react'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Flag from '../ui/Flag'

// En el Mundial 2026 con 48 equipos y 12 grupos, los 8 mejores terceros avanzan
const MAX_THIRDS = 8
const TOTAL_GROUPS = 12

// Letras de los grupos disponibles
const groupLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']

export default function ThirdsPicker({ groupPredictions, selectedThirds, onUpdate }) {
  // Obtener el tercer lugar de cada grupo basado en las predicciones de fase de grupos
  const thirdPlaceTeams = groupLetters.map(letter => {
    const groupTeams = groupPredictions?.[letter]
    return {
      group: letter,
      team: groupTeams ? groupTeams[2] : `3° Grupo ${letter}`,
      hasGroupPrediction: !!groupTeams,
    }
  })

  // Alternar selección de un tercer lugar
  const toggleSelection = (group) => {
    const current = selectedThirds || []
    if (current.includes(group)) {
      // Deseleccionar
      onUpdate(current.filter(g => g !== group))
    } else if (current.length < MAX_THIRDS) {
      // Seleccionar (si no se ha alcanzado el máximo)
      onUpdate([...current, group])
    }
  }

  const selected = selectedThirds || []
  const remaining = MAX_THIRDS - selected.length

  return (
    <div>
      {/* Encabezado */}
      <div className="mb-6">
        <h3 className="text-xl font-display font-bold text-text-primary mb-2">
          Mejores Terceros
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          Selecciona los {MAX_THIRDS} terceros lugares que crees que clasificarán a la siguiente ronda.
        </p>

        {/* Indicador de selección */}
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {Array.from({ length: MAX_THIRDS }).map((_, i) => (
              <motion.div
                key={i}
                className={`w-3 h-3 rounded-full transition-colors ${
                  i < selected.length ? 'bg-cyan' : 'bg-surface-card border border-border-default'
                }`}
                animate={i < selected.length ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>
          <span className="text-sm font-mono text-text-secondary">
            {selected.length}/{MAX_THIRDS} seleccionados
          </span>
        </div>

        {remaining > 0 && selected.length > 0 && (
          <p className="mt-2 text-xs text-warning flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Faltan {remaining} por seleccionar
          </p>
        )}
      </div>

      {/* Cuadrícula de terceros */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {thirdPlaceTeams.map((item, i) => {
          const isSelected = selected.includes(item.group)
          const isDisabled = !isSelected && selected.length >= MAX_THIRDS

          return (
            <motion.button
              key={item.group}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.03 }}
              onClick={() => !isDisabled && toggleSelection(item.group)}
              disabled={isDisabled}
              className={`
                relative p-4 rounded-xl border-2 text-left
                transition-all duration-200 cursor-pointer
                disabled:opacity-40 disabled:cursor-not-allowed
                ${isSelected
                  ? 'border-cyan bg-cyan/10 shadow-cyan'
                  : 'border-border-default bg-surface-card hover:border-border-strong hover:bg-surface-card-hover'
                }
              `}
            >
              {/* Indicador de selección */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 w-5 h-5 rounded-full bg-cyan flex items-center justify-center"
                >
                  <Check className="w-3 h-3 text-navy-dark" />
                </motion.div>
              )}

              {/* Grupo */}
              <Badge variant={isSelected ? 'cyan' : 'default'} size="sm" className="mb-2">
                Grupo {item.group}
              </Badge>

              {/* Equipo */}
              <p className={`text-sm font-medium truncate flex items-center justify-center ${isSelected ? 'text-cyan' : 'text-text-primary'}`}>
                {typeof item.team === 'object' && item.team ? (
                  <>
                    <Flag code={item.team.bandera} className="w-4 h-3 mr-1.5 object-cover rounded shadow-sm shrink-0" />
                    <span>{item.team.nombre}</span>
                  </>
                ) : (
                  item.team
                )}
              </p>

              {!item.hasGroupPrediction && (
                <p className="text-[0.6rem] text-text-muted mt-1">
                  Completa el grupo primero
                </p>
              )}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
